import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button, Dropdown, Badge } from 'flowbite-react';
import { 
  HiUser, 
  HiLogout, 
  HiShieldCheck, 
  HiPhone,
  HiInformationCircle,
  HiCog,
  HiMoon,
  HiSun,
  HiMenu,
  HiChevronDown
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface HeaderProps {
  className?: string;
  onMenuClick?: () => void;
}

const APP_VERSION = '1.0.0';

export default function Header({ className, onMenuClick }: HeaderProps) {
  const { user, userProfile, signOut } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  const currentTheme = mounted ? (resolvedTheme || theme || 'light') : 'light';

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleViewProfile = () => {
    navigate('/dashboard');
  };

  const toggleTheme = () => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  const getStatusBadge = () => {
    if (!userProfile) return null;
    
    const statusColor = {
      active: 'success',
      pending_approval: 'warning',
      inactive: 'failure',
    }[userProfile.status] || 'failure';
    
    const statusLabel = {
      active: 'Activo',
      pending_approval: 'Pendiente',
      inactive: 'Inactivo',
    }[userProfile.status] || 'Inactivo';
    
    return (
      <Badge color={statusColor as any} className="text-xs">
        {statusLabel}
      </Badge>
    );
  };

  const getRoleBadge = () => {
    if (!userProfile) return null;
    
    const roleConfig = {
      superadmin: { color: 'blue' as const, label: 'Superadmin', icon: HiShieldCheck },
      admin: { color: 'gray' as const, label: 'Admin', icon: HiUser },
      user: { color: 'light' as const, label: 'Usuario', icon: HiUser },
    };

    const config = roleConfig[userProfile.role] || roleConfig.user;
    const Icon = config.icon;
    
    return (
      <Badge color={config.color} icon={Icon} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  if (!user) return null;

  return (
    <header className={`sticky top-0 z-50 w-full border-b bg-white dark:bg-gray-800 shadow-sm ${className || ''}`}>
      <div className="w-full flex h-16 items-center justify-between px-4 lg:px-6">
        {/* Botón Menú + Logo - Izquierda */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {onMenuClick && (
            <Button
              color="light"
              size="sm"
              onClick={onMenuClick}
              aria-label="Toggle menu"
            >
              <HiMenu className="h-5 w-5" />
            </Button>
          )}
          <img 
            src="/logo.svg" 
            alt="Domus Secura" 
            className="h-8 w-auto"
          />
          <div className="hidden sm:block">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Domus Secura</h1>
            <p className="text-xs text-gray-600 dark:text-gray-400">Sistema de Seguridad</p>
          </div>
        </div>

        {/* Controles Derecha - Toggle y Dropdown */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Theme Toggle - A la izquierda del dropdown */}
          {mounted && (
            <Button
              color="light"
              size="sm"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="flex items-center justify-center"
            >
              {currentTheme === 'dark' ? (
                <HiSun className="h-5 w-5" />
              ) : (
                <HiMoon className="h-5 w-5" />
              )}
            </Button>
          )}

          {/* User Menu Dropdown - Totalmente a la derecha */}
          <Dropdown
            label=""
            dismissOnClick={false}
            renderTrigger={() => (
              <Button color="light" className="flex items-center gap-2 px-3 py-2">
                <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <HiUser className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                </div>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {userProfile?.name || user.email?.split('@')[0] || 'Usuario'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{user.email}</span>
                </div>
                <HiChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 ml-1" />
              </Button>
            )}
          >
            {/* Header del dropdown */}
            <div className="px-4 py-3 text-sm text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600">
              <div className="flex flex-col space-y-1">
                <div className="font-medium">
                  {userProfile?.name || user.email?.split('@')[0] || 'Usuario'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                {userProfile && (
                  <div className="flex items-center gap-2 mt-2">
                    {getRoleBadge()}
                    {getStatusBadge()}
                  </div>
                )}
              </div>
            </div>

            {/* Información del perfil */}
            {userProfile && userProfile.phone && (
              <>
                <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <HiPhone className="h-4 w-4 flex-shrink-0" />
                  <span>{userProfile.phone}</span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600"></div>
              </>
            )}

            {/* Opciones del menú con iconos */}
            <div className="py-1">
              <button
                onClick={handleViewProfile}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 transition-colors"
              >
                <HiUser className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <span>Mi Perfil</span>
              </button>
              <button
                onClick={() => navigate('/admin/settings')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 transition-colors"
              >
                <HiCog className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <span>Configuración</span>
              </button>
              <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <HiInformationCircle className="h-4 w-4 flex-shrink-0" />
                <span>Versión {APP_VERSION}</span>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-600"></div>
            <div className="py-1">
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 transition-colors"
              >
                <HiLogout className="h-4 w-4 flex-shrink-0" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </Dropdown>
        </div>
      </div>
    </header>
  );
}
